import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft,
  FileText,
  User,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Play,
  Loader2,
  AlertCircle,
  Stethoscope,
  Pill,
  Heart,
  TestTube,
  ClipboardList,
  Shield,
  History,
  Download,
  Send,
  Copy,
  FileDown,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Eye,
  Image,
  X,
  Printer,
  Pencil,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { diffWords } from '../lib/textDiff';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

const statusConfig = {
  new: { label: 'New', class: 'bg-blue-100 text-blue-700', icon: FileText },
  parsing: { label: 'Parsing', class: 'bg-purple-100 text-purple-700', icon: Loader2 },
  awaiting_context: { label: 'Awaiting Context', class: 'bg-indigo-100 text-indigo-700', icon: Clock },
  analyzing: { label: 'Analyzing', class: 'bg-cyan-100 text-cyan-700', icon: Activity },
  awaiting_review: { label: 'Awaiting Review', class: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  completed: { label: 'Completed', class: 'bg-green-100 text-green-700', icon: CheckCircle },
  archived: { label: 'Archived', class: 'bg-slate-100 text-slate-700', icon: History },
};

const flagConfig = {
  normal: { label: 'Normal', class: 'bg-green-100 text-green-700 border-green-200' },
  borderline: { label: 'Borderline', class: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  abnormal: { label: 'Abnormal', class: 'bg-orange-100 text-orange-700 border-orange-200' },
  critical: { label: 'Critical', class: 'bg-red-100 text-red-700 border-red-200' },
};

// soap_notes is free-form JSON straight from the LLM — nothing validates it server-side,
// so any field the prompt asks for as a string can arrive as an array or an object.
// Handing one to React as a child throws and, with no error boundary above this page,
// takes the whole screen white. Everything rendered out of soap_notes goes through here.
const toDisplayText = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map(toDisplayText).filter(Boolean).join('\n');
  }
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([key, val]) => `${key.replace(/_/g, ' ')}: ${toDisplayText(val)}`)
      .filter(Boolean)
      .join(', ');
  }
  return String(value);
};

const displayTextOr = (value, fallback = 'Not available') => toDisplayText(value) || fallback;

// Reference ranges display the report's own printed text when the extraction captured
// it (reference_range: "<200", ">55", "1.5 - 4.1"), because one-sided and banded ranges
// don't round-trip through the numeric bounds — "low - high" would render as "55 - null".
// Which side of the resolved reference band a value sits on — the badge reads
// "Abnormal (Low)" / "Borderline (High)" instead of a bare category.
const flagDirection = (obs) => {
  const value = obs?.value_numeric;
  if (value === null || value === undefined) return '';
  if (obs.reference_low !== null && obs.reference_low !== undefined && value < obs.reference_low) return ' (Low)';
  if (obs.reference_high !== null && obs.reference_high !== undefined && value > obs.reference_high) return ' (High)';
  return '';
};

const formatReferenceRange = (obs) => {
  const text = typeof obs?.reference_range === 'string' ? obs.reference_range.trim() : '';
  if (text && !['n/a', 'none', 'null', '-'].includes(text.toLowerCase())) return text;
  const low = obs?.reference_low;
  const high = obs?.reference_high;
  if (low !== null && low !== undefined && high !== null && high !== undefined) return `${low} - ${high}`;
  if (high !== null && high !== undefined) return `< ${high}`;
  if (low !== null && low !== undefined) return `> ${low}`;
  return '-';
};

// Only a list of per-parameter objects earns the table. An array of plain strings
// ("HbA1c: 7.2% (Ref: <5.7%) - HIGH") is already formatted prose and would otherwise
// render as a row of Unknown / - / N/A placeholders.
const isObjectiveTable = (value) =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((item) => item && typeof item === 'object' && !Array.isArray(item));

// Callers reach this with unvalidated array items, so a null or a bare string has to
// fall through to the placeholder rather than throw on property access.
const objectiveCell = (item, keys, fallback) => {
  if (!item || typeof item !== 'object') return fallback;
  const key = keys.find((k) => item[k] !== undefined && item[k] !== null && item[k] !== '');
  return key === undefined ? fallback : displayTextOr(item[key], fallback);
};

// The patient record is the source of truth. The report row also carries denormalised
// patient_name / patient_mrn, but they are frequently blank — reading them first is why
// the SOAP preview showed "Unknown Patient" for a report whose patient is on file. Same
// precedence the patient-summary PDF endpoint uses.
const patientIdentity = (data) => ({
  name: data?.patient?.full_name || data?.report?.patient_name || 'Unknown Patient',
  mrn: data?.patient?.mrn_member_id || data?.report?.patient_mrn || 'N/A',
});

/**
 * Collapse detailed-result rows that share a heading into one row.
 *
 * A radiology summary arrives as one row per narrative finding, so two impressions and
 * three recommendations repeated "Impression / Clinical Impression / INFO" and
 * "Recommendation / Follow-up / ACTION" down the table with only the result text
 * differing. Rows only merge when the test, reference and status are all identical —
 * which is exactly what makes them the same heading — so lab rows, each describing a
 * different test, never group. Groups keep the order they first appear.
 */
const groupDetailedResults = (rows) => {
  const groups = [];
  const index = new Map();

  for (const row of rows ?? []) {
    const test = toDisplayText(row?.test) || 'Unknown';
    const reference = toDisplayText(row?.reference_range ?? row?.reference);
    const status = toDisplayText(row?.status);
    const key = JSON.stringify([test, reference, status]);

    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ test, reference, status, results: [] });
    }
    groups[index.get(key)].results.push(toDisplayText(row?.result));
  }
  return groups;
};

const OBJECTIVE_LINE = /^(.+?):\s*(.+?)\s*(?:\(Ref:\s*([^)]+)\))?\s*-\s*(\w+)$/i;

/**
 * Rows for the objective results table, or null when the objective is prose.
 *
 * Structured data tabulates directly. Edited text tabulates only when every line is a
 * "Test: value (Ref: range) - STATUS" result — the shape the editor is seeded with. A
 * partial match means prose, and prose is left as prose: forcing it into columns would
 * invent results that were never measured.
 */
const objectiveTableRows = (objective) => {
  if (isObjectiveTable(objective)) {
    return objective.map((item) => ({
      parameter: objectiveCell(item, ['parameter', 'name'], 'Unknown'),
      value: objectiveCell(item, ['value'], '-'),
      reference: objectiveCell(item, ['reference', 'ref'], 'N/A'),
      status: objectiveCell(item, ['status'], 'normal'),
    }));
  }
  if (typeof objective !== 'string') return null;

  const lines = objective.split('\n').map((line) => line.trim()).filter(Boolean);
  if (!lines.length) return null;

  const matches = lines.map((line) => line.match(OBJECTIVE_LINE));
  if (matches.some((match) => !match)) return null;

  return matches.map((match) => ({
    parameter: match[1].trim(),
    value: match[2].trim(),
    reference: match[3] ? match[3].trim() : 'N/A',
    status: match[4].trim().toLowerCase(),
  }));
};

// The print template interpolates LLM text straight into markup, where an unescaped
// "<" in something like "Hb < 10 g/dL" would eat the rest of the document.
const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const soapTextHtml = (value) =>
  `<div style="white-space: pre-wrap;">${escapeHtml(displayTextOr(value))}</div>`;

// ============== EDITABLE SECTIONS ==============
// The three AI-generated sections below are editable as plain text: a clinician
// corrects the wording, and every save appends a version that can be diffed against
// the one before it. These serializers define what the AI's own output looks like as
// text — they produce version 1 of a section, and the textarea the clinician types
// into. Once a section has been edited, its saved text is what the page renders.

// Extracted Observations is deliberately absent: it stays read-only and keeps its
// table, because a free-text edit box cannot round-trip a structured result grid.
const SECTIONS = {
  clinicalAnalysis: 'clinical_analysis',
  patientSummary: 'patient_summary',
};

// Mirrors the report:update rule in backend/middleware/rbac.py — a technician uploads
// reports but does not amend clinical content. The backend is what enforces this;
// hiding the button only avoids offering an action that would 403.
const SECTION_EDIT_ROLES = ['super_admin', 'sys_admin', 'org_admin', 'dept_admin', 'clinician'];

const CURRENT_VERSION_OPTION = 'current';

// A SOAP objective arrives either as prose or as a list of per-parameter objects. The
// latter reads as noise through the generic object coercion, so give it the same
// one-line-per-parameter shape the observations section uses.
const serializeSoapObjective = (objective) => {
  if (!isObjectiveTable(objective)) return displayTextOr(objective);
  return objective
    .map((item) => {
      const parameter = objectiveCell(item, ['parameter', 'name'], 'Unknown');
      const value = objectiveCell(item, ['value'], '-');
      const reference = objectiveCell(item, ['reference', 'ref'], 'N/A');
      const status = objectiveCell(item, ['status'], 'normal');
      return `${parameter}: ${value} (Ref: ${reference}) - ${status.toUpperCase()}`;
    })
    .join('\n');
};

const serializeClinicalAnalysis = (analysis) => {
  if (!analysis) return '';
  const blocks = [`Clinician Summary:\n${displayTextOr(analysis.clinician_summary)}`];

  const soap = analysis.soap_notes;
  if (soap) {
    blocks.push(
      [
        'SOAP Notes',
        `Subjective:\n${displayTextOr(soap.subjective)}`,
        `Objective:\n${serializeSoapObjective(soap.objective)}`,
        `Assessment:\n${displayTextOr(soap.assessment)}`,
        `Plan:\n${displayTextOr(soap.plan)}`,
      ].join('\n\n'),
    );
  }

  if (analysis.abnormal_findings_json?.length > 0) {
    const findings = analysis.abnormal_findings_json
      .map((f) => {
        const finding = displayTextOr(f?.finding, 'Unknown finding');
        const significance = displayTextOr(f?.significance, '');
        const severity = displayTextOr(f?.severity, '');
        const detail = [significance, severity && `(${severity})`].filter(Boolean).join(' ');
        return `- ${finding}${detail ? ` — ${detail}` : ''}`;
      })
      .join('\n');
    blocks.push(`Abnormal Findings:\n${findings}`);
  }

  return blocks.join('\n\n');
};

const serializePatientSummary = (summary) => {
  if (!summary) return '';
  const blocks = [displayTextOr(summary.plain_language_summary)];

  const itemLines = (items) =>
    items
      .map((item) => `- ${displayTextOr(typeof item === 'object' && item ? item.item : item, '')}`)
      .filter((line) => line !== '- ')
      .join('\n');

  if (summary.normal_items_json?.length > 0) {
    blocks.push(`Normal Results:\n${itemLines(summary.normal_items_json)}`);
  }
  if (summary.borderline_items_json?.length > 0) {
    blocks.push(`Borderline Results:\n${itemLines(summary.borderline_items_json)}`);
  }
  if (summary.abnormal_items_json?.length > 0) {
    blocks.push(`Abnormal Results:\n${itemLines(summary.abnormal_items_json)}`);
  }
  if (summary.next_steps_text) {
    blocks.push(`Recommended Next Steps:\n${displayTextOr(summary.next_steps_text)}`);
  }

  return blocks.join('\n\n');
};

// ============== READING EDITED TEXT BACK INTO SHAPE ==============
// Editing is free text, but a section must still render as its table, labelled blocks
// and plan callout once saved — a wall of text is not a clinical report. So saved text
// is parsed back into the same shape the AI produces and handed to the very same
// renderers. Anything these parsers cannot place degrades to plain text rather than
// being lost.

// A heading may be retitled in passing ("Clinician Summary:" -> "Clinician Summary of
// the patient"), so match on the line's opening label rather than the whole line. The
// slack bounds how much trailing text still counts as a heading, which keeps a
// paragraph opening with "Plan to recheck HbA1c in three months..." from being read
// as the Plan heading.
const HEADING_TRAILING_SLACK = 30;
const PREAMBLE = '__preamble';

const matchHeading = (line, labels) => {
  const cleaned = line.trim().replace(/:\s*$/, '');
  const lower = cleaned.toLowerCase();
  return (
    labels.find((label) => {
      if (!lower.startsWith(label)) return false;
      const rest = cleaned.slice(label.length).replace(/^[\s:—–-]+/, '');
      return rest.length <= HEADING_TRAILING_SLACK;
    }) ?? null
  );
};

const splitByHeadings = (text, labels) => {
  const out = {};
  let current = PREAMBLE;
  let buffer = [];

  const flush = () => {
    const joined = buffer.join('\n').trim();
    if (joined) out[current] = out[current] ? `${out[current]}\n${joined}` : joined;
    buffer = [];
  };

  for (const line of String(text ?? '').split('\n')) {
    const label = matchHeading(line, labels);
    if (label) {
      flush();
      current = label;
    } else {
      buffer.push(line);
    }
  }
  flush();
  return out;
};

const bulletItems = (block) =>
  String(block ?? '')
    .split('\n')
    .map((line) => line.replace(/^\s*[-•*]\s*/, '').trim())
    .filter(Boolean);

const CLINICAL_ANALYSIS_LABELS = [
  'clinician summary',
  'soap notes',
  'subjective',
  'objective',
  'assessment',
  'plan',
  'abnormal findings',
];

const parseClinicalAnalysisText = (text) => {
  const parts = splitByHeadings(text, CLINICAL_ANALYSIS_LABELS);
  // "<finding> — <significance> (<severity>)". The trailing period is tolerated because
  // a clinician editing the text naturally ends the sentence after the bracket; without
  // it the severity failed to match, so it stayed buried in the significance and the
  // severity badge rendered blank. The period is only consumed when a severity bracket
  // actually matched, so a significance that simply ends in a full stop keeps it.
  const findings = bulletItems(parts['abnormal findings']).map((line) => {
    const match = line.match(/^(.*?)\s+[—–-]\s+(.*?)(?:\s*\(([^)]*)\)\s*\.?)?\s*$/);
    if (!match) return { finding: line, significance: '', severity: '' };
    return { finding: match[1].trim(), significance: match[2].trim(), severity: (match[3] || '').trim() };
  });

  const soapNotes = {
    subjective: parts.subjective || '',
    // Left as a string on purpose: the objective renderer already parses
    // "Test: value (Ref: range) - STATUS" lines back into the results table.
    objective: parts.objective || '',
    assessment: parts.assessment || '',
    plan: parts.plan || '',
  };
  // Mirror the serializer, which omits the SOAP block when the analysis has none.
  // Returning an empty-but-truthy object instead would satisfy the card's
  // `soap_notes &&` guard and graft a block of "Not available" rows onto a report that
  // never had SOAP notes — a radiology analysis carries a summary and findings only.
  const hasSoapContent = Object.values(soapNotes).some(Boolean);

  return {
    // Text typed above any heading is still the summary — losing it would silently drop
    // whatever the clinician wrote first.
    clinician_summary: parts['clinician summary'] || parts[PREAMBLE] || '',
    soap_notes: hasSoapContent ? soapNotes : null,
    abnormal_findings_json: findings,
  };
};

const PATIENT_SUMMARY_LABELS = ['normal results', 'borderline results', 'abnormal results', 'recommended next steps', 'next steps'];

const parsePatientSummaryText = (text) => {
  const parts = splitByHeadings(text, PATIENT_SUMMARY_LABELS);
  return {
    plain_language_summary: parts[PREAMBLE] || '',
    normal_items_json: bulletItems(parts['normal results']),
    borderline_items_json: bulletItems(parts['borderline results']),
    abnormal_items_json: bulletItems(parts['abnormal results']),
    next_steps_text: parts['recommended next steps'] || parts['next steps'] || '',
  };
};

// Additions are amber on black and deletions are struck through in red, so the two
// read differently even for a colour-blind reviewer.
const DiffView = ({ previousText, currentText }) => {
  // Diffing is O(n*m) and runs tens of milliseconds on a long section; without this
  // it would re-run on every unrelated re-render of the page.
  const segments = React.useMemo(
    () => diffWords(previousText, currentText),
    [previousText, currentText],
  );
  return (
    <p className="text-sm whitespace-pre-wrap leading-relaxed">
      {segments.map((segment, idx) => {
        if (segment.type === 'added') {
          return (
            <mark key={idx} style={{ backgroundColor: '#FFC107', color: '#000' }} className="rounded-sm">
              {segment.text}
            </mark>
          );
        }
        if (segment.type === 'removed') {
          return (
            <del key={idx} className="text-red-600 line-through decoration-red-600">
              {segment.text}
            </del>
          );
        }
        return <span key={idx} className="text-slate-700">{segment.text}</span>;
      })}
    </p>
  );
};

/**
 * In-line editing and version history for one report section.
 *
 * Returns the header `controls` and the section `body` separately, because each
 * section's buttons live in its card header while its content lives in the card body
 * — a wrapper component cannot straddle both without rewriting all three layouts.
 * That makes this a hook, so every caller must invoke it unconditionally.
 *
 * The caller always renders its own structured view when `showStructured` is true,
 * feeding it either the AI's data or `editedText` parsed back into the same shape — an
 * edited section keeps its table, labels and callouts. `body` is only the editor or a
 * version diff, both of which are inherently plain text.
 */
const useEditableSection = ({ sectionKey, baselineText, versions, canEdit, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const textareaRef = useRef(null);

  // versions arrive latest-first
  const latest = versions?.[0] ?? null;
  const currentText = latest ? latest.content : baselineText;
  const hasHistory = (versions?.length ?? 0) > 0;

  useEffect(() => {
    if (editing && textareaRef.current) {
      const input = textareaRef.current;
      input.focus();
      // Land the caret at the end rather than selecting everything, so the first
      // keystroke doesn't wipe the section.
      input.setSelectionRange(input.value.length, input.value.length);
    }
  }, [editing]);

  const startEdit = () => {
    setDraft(currentText);
    setSelectedVersion(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft('');
  };

  const saveEdit = async () => {
    const content = draft.trim();
    if (!content) {
      toast.error('Content cannot be empty');
      return;
    }
    if (content === currentText.trim()) {
      toast.info('No changes to save');
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(sectionKey, content, baselineText);
      setEditing(false);
      setDraft('');
      setSelectedVersion(null);
      toast.success('Changes saved successfully.');
    } catch (error) {
      // FastAPI sends a string detail; a 422 sends an array of objects, and an
      // unhandled server error sends plain text with no detail at all. Only a string
      // is safe to hand to the toast — anything else falls back to the status code,
      // which at least says where to look instead of swallowing the failure.
      const detail = error?.response?.data?.detail;
      const status = error?.response?.status;
      console.error('Section save failed:', status, error?.response?.data ?? error);
      if (typeof detail === 'string' && detail) {
        toast.error(detail);
      } else {
        toast.error(status ? `Failed to save changes (HTTP ${status})` : 'Failed to save changes');
      }
    } finally {
      setSaving(false);
    }
  };

  // A refetch can drop history out from under a selection, so never index blindly.
  const selected = selectedVersion && versions
    ? versions.find((v) => v.version === selectedVersion) ?? null
    : null;
  const previousOfSelected = selected
    ? versions.find((v) => v.version === selected.version - 1) ?? null
    : null;

  const controls = (
    <div className="flex items-center gap-2">
      {hasHistory && !editing && (
        // "Current" is a distinct option rather than just defaulting to the latest
        // version: Radix fires no change event when you pick the value already shown,
        // so without it the newest version could never be selected to view its diff.
        <Select
          value={selectedVersion === null ? CURRENT_VERSION_OPTION : String(selectedVersion)}
          onValueChange={(value) =>
            setSelectedVersion(value === CURRENT_VERSION_OPTION ? null : Number(value))
          }
        >
          <SelectTrigger className="h-7 w-[150px] text-xs" data-testid={`version-select-${sectionKey}`}>
            <History className="w-3 h-3 mr-1 shrink-0" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CURRENT_VERSION_OPTION} className="text-xs">
              Current (v{latest.version})
            </SelectItem>
            {versions.map((v) => (
              <SelectItem key={v.version} value={String(v.version)} className="text-xs">
                Version {v.version}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {canEdit && !editing && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={startEdit}
          data-testid={`edit-${sectionKey}-btn`}
        >
          <Pencil className="w-3 h-3 mr-1" />
          Edit
        </Button>
      )}

      {editing && (
        <>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={cancelEdit}
            disabled={saving}
            data-testid={`cancel-${sectionKey}-btn`}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            className="h-7 text-xs"
            onClick={saveEdit}
            disabled={saving}
            data-testid={`save-${sectionKey}-btn`}
          >
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Save Changes
          </Button>
        </>
      )}
    </div>
  );

  let body = null;
  let showStructured = false;
  if (editing) {
    body = (
      <Textarea
        ref={textareaRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="min-h-[240px] font-mono text-xs leading-relaxed"
        data-testid={`editor-${sectionKey}`}
      />
    );
  } else if (selected) {
    body = (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-muted-foreground">
            {previousOfSelected
              ? `Changes from Version ${previousOfSelected.version} to Version ${selected.version}`
              : `Version ${selected.version} — original AI-generated content`}
            {' · '}
            {selected.edited_by_name} · {new Date(selected.edited_at).toLocaleString()}
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={() => setSelectedVersion(null)}
            data-testid={`exit-compare-${sectionKey}-btn`}
          >
            Back to current
          </Button>
        </div>
        {previousOfSelected ? (
          <DiffView previousText={previousOfSelected.content} currentText={selected.content} />
        ) : (
          <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{selected.content}</p>
        )}
      </div>
    );
  } else {
    showStructured = true;
  }

  const footer =
    showStructured && hasHistory ? (
      <p className="text-xs text-muted-foreground pt-1">
        Version {latest.version} · edited by {latest.edited_by_name} ·{' '}
        {new Date(latest.edited_at).toLocaleString()}
      </p>
    ) : null;

  return {
    controls,
    body,
    footer,
    showStructured,
    editing,
    // Latest saved text, or null when the section has never been edited. The caller
    // parses this back into its structured shape rather than printing it raw.
    editedText: latest ? latest.content : null,
  };
};

export const ReportDetailPage = () => {
  const { reportId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [data, setData] = useState(null);
  const [clinicianNote, setClinicianNote] = useState('');
  const [reportContent, setReportContent] = useState('');
  const [showProcessDialog, setShowProcessDialog] = useState(false);
  const [showOriginalDoc, setShowOriginalDoc] = useState(false);
  const [originalDocUrl, setOriginalDocUrl] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [showSOAPPreview, setShowSOAPPreview] = useState(false);
  const [showPatientSummaryPreview, setShowPatientSummaryPreview] = useState(false);
  const [patientSummaryData, setPatientSummaryData] = useState(null);
  const [loadingPatientSummary, setLoadingPatientSummary] = useState(false);
  const [sectionVersions, setSectionVersions] = useState({});

  useEffect(() => {
    fetchReportDetail();
  }, [reportId]);

  const fetchReportDetail = async () => {
    try {
      const response = await api.get(`/reports/${reportId}`);
      setData(response.data);
      // Load existing raw content if available
      if (response.data?.report?.raw_content) {
        setReportContent(response.data.report.raw_content);
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Failed to load report details');
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionVersions = useCallback(async () => {
    try {
      const response = await api.get(`/reports/${reportId}/sections/versions`);
      setSectionVersions(response.data || {});
    } catch (error) {
      // A section that has never been edited has no history, and the page is still
      // fully usable read-only — so this failure must not block the report itself.
      console.error('Failed to load section version history:', error);
    }
  }, [api, reportId]);

  useEffect(() => {
    fetchSectionVersions();
  }, [fetchSectionVersions]);

  const saveSectionVersion = useCallback(
    async (section, content, baselineText) => {
      await api.post(`/reports/${reportId}/sections/${section}/versions`, {
        content,
        baseline_content: baselineText,
      });
      await fetchSectionVersions();
    },
    [api, reportId, fetchSectionVersions],
  );

  // The clinical analysis as it should be read and exported: a clinician's latest saved
  // edit where one exists, otherwise the AI's own record, parsed back into the AI's
  // shape. Derived here, above the handlers, so the card, the SOAP preview and the
  // printed PDF all render the same object and cannot drift apart.
  const analysisForDisplay = useMemo(() => {
    const editedText = sectionVersions[SECTIONS.clinicalAnalysis]?.[0]?.content;
    return editedText ? parseClinicalAnalysisText(editedText) : data?.clinical_analysis;
  }, [sectionVersions, data]);

  // Likewise for the patient summary. Derived here rather than after the early returns
  // because radiology next steps are read out of it by the Clinical Analysis preview and
  // its PDF, which are built above — and they must follow a clinician's edit, not the
  // superseded record.
  const summaryForDisplay = useMemo(() => {
    const editedText = sectionVersions[SECTIONS.patientSummary]?.[0]?.content;
    return editedText ? parsePatientSummaryText(editedText) : data?.patient_summary;
  }, [sectionVersions, data]);

  const loadOriginalDocument = async () => {
    setLoadingDoc(true);
    try {
      const response = await api.get(`/reports/${reportId}/file`, {
        responseType: 'blob',
        validateStatus: (status) => status < 500 // Don't throw for 4xx errors
      });
      
      // Check if we got an error response (JSON error message as blob)
      if (response.status === 404) {
        toast.error('Original document not available for this report. Only newly uploaded reports have this feature.');
        return;
      }
      
      if (response.status >= 400) {
        toast.error('Failed to load original document');
        return;
      }
      
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      setOriginalDocUrl(url);
      setShowOriginalDoc(true);
    } catch (error) {
      console.error('Error loading document:', error);
      if (error.response?.status === 404) {
        toast.error('Original document not available for this report');
      } else {
        toast.error('Failed to load original document. This feature is only available for newly uploaded reports.');
      }
    } finally {
      setLoadingDoc(false);
    }
  };

  const closeOriginalDoc = () => {
    setShowOriginalDoc(false);
    if (originalDocUrl) {
      URL.revokeObjectURL(originalDocUrl);
      setOriginalDocUrl(null);
    }
  };

  // The preview button sits on the Clinical Analysis strip and leads with the clinician
  // summary, so an analysis carrying a summary but no SOAP notes — a radiology report,
  // typically — is still worth previewing.
  const hasAnalysisPreview = Boolean(
    analysisForDisplay &&
      (analysisForDisplay.soap_notes || toDisplayText(analysisForDisplay.clinician_summary)),
  );

  // Show Clinical Analysis preview modal
  const openSOAPPreview = () => {
    if (!hasAnalysisPreview) {
      toast.error('No clinical analysis available to preview');
      return;
    }
    setShowSOAPPreview(true);
  };

  // Download/Print Clinical Analysis as PDF
  const downloadSOAPNotes = () => {
    const report = data?.report;
    // Follows the latest saved edit: a clinician who corrects the notes must not have
    // the AI's superseded wording printed and handed on.
    const soap_notes = analysisForDisplay?.soap_notes;
    const objectiveRows = objectiveTableRows(soap_notes?.objective);
    const clinicianSummary = toDisplayText(analysisForDisplay?.clinician_summary);
    const abnormalFindings = analysisForDisplay?.abnormal_findings_json ?? [];
    // Radiology only: its next steps and detailed findings belong to the clinician, so
    // they print here rather than in the patient's summary.
    const isRadiology = data?.report?.report_type === 'radiology' || data?.report?.file_type === 'image';
    const nextSteps = isRadiology ? toDisplayText(summaryForDisplay?.next_steps_text) : '';
    const detailedGroups = isRadiology ? groupDetailedResults(data?.detailed_results) : [];
    const { name: patientName, mrn: patientMrn } = patientIdentity(data);
    const patientDob = data?.patient?.dob || report?.patient_dob;

    if (!hasAnalysisPreview) {
      toast.error('No clinical analysis available to download');
      return;
    }

    // Create print window content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>SOAP Notes - ${escapeHtml(patientName)}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
            padding: 40px; 
            color: #1a1a1a;
            line-height: 1.6;
          }
          .header { 
            border-bottom: 2px solid #0066cc; 
            padding-bottom: 20px; 
            margin-bottom: 30px; 
          }
          .logo { 
            font-size: 24px; 
            font-weight: bold; 
            color: #0066cc; 
            margin-bottom: 5px;
          }
          .report-info { 
            display: flex; 
            justify-content: space-between; 
            margin-top: 15px;
            font-size: 14px;
            color: #555;
          }
          .patient-info {
            background: #f5f5f5;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 25px;
          }
          .patient-info h3 { 
            font-size: 14px; 
            color: #666; 
            margin-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .patient-name { 
            font-size: 20px; 
            font-weight: bold; 
            color: #1a1a1a;
          }
          .patient-details { 
            font-size: 14px; 
            color: #555; 
            margin-top: 5px;
          }
          .soap-section { 
            margin-bottom: 25px; 
            page-break-inside: avoid;
          }
          .soap-label { 
            font-size: 12px; 
            font-weight: 600; 
            text-transform: uppercase; 
            letter-spacing: 1px;
            color: #0066cc; 
            margin-bottom: 8px;
            padding-bottom: 5px;
            border-bottom: 1px solid #e0e0e0;
          }
          .soap-content { 
            font-size: 14px; 
            color: #333;
            padding-left: 10px;
          }
          .plan-section {
            background: #e6f2ff;
            padding: 15px;
            border-radius: 8px;
            border-left: 4px solid #0066cc;
          }
          .plan-section .soap-label {
            color: #004d99;
            border-bottom: none;
          }
          .footer { 
            margin-top: 40px; 
            padding-top: 20px; 
            border-top: 1px solid #ddd; 
            font-size: 11px; 
            color: #888;
            text-align: center;
          }
          .disclaimer {
            background: #fff3cd;
            border: 1px solid #ffc107;
            padding: 12px;
            border-radius: 6px;
            margin-bottom: 25px;
            font-size: 12px;
            color: #856404;
          }
          @media print {
            body { padding: 20px; }
            .soap-section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">meragoAnalyze</div>
          <div style="font-size: 12px; color: #666;">Clinical Decision Support - SOAP Notes</div>
          <div class="report-info">
            <span>Report ID: ${report?.report_id?.substring(0, 8) || 'N/A'}</span>
            <span>Type: ${report?.report_type || 'N/A'}</span>
            <span>Date: ${report?.report_date ? new Date(report.report_date).toLocaleDateString() : new Date().toLocaleDateString()}</span>
          </div>
        </div>

        <div class="patient-info">
          <h3>Patient Information</h3>
          <div class="patient-name">${escapeHtml(patientName)}</div>
          <div class="patient-details">
            MRN: ${escapeHtml(patientMrn)} |
            DOB: ${patientDob ? new Date(patientDob).toLocaleDateString() : 'N/A'}
          </div>
        </div>

        ${clinicianSummary ? `
        <div class="soap-section">
          <div class="soap-label">Clinician Summary</div>
          <div class="soap-content">${soapTextHtml(clinicianSummary)}</div>
        </div>` : ''}

        <div class="disclaimer">
          <strong>Clinical Decision Support Only:</strong> This analysis is for decision support purposes. 
          It does not provide definitive diagnoses or prescribe treatment. Always consult with qualified healthcare professionals.
        </div>

        ${!soap_notes ? '' : `
        <div class="soap-section">
          <div class="soap-label">Subjective</div>
          <div class="soap-content">${soapTextHtml(soap_notes.subjective)}</div>
        </div>

        <div class="soap-section">
          <div class="soap-label">Objective</div>
          <div class="soap-content">${
            objectiveRows
              ? `<table style="width:100%;border-collapse:collapse;font-size:13px;">
                      <thead>
                        <tr style="background:#f0f0f0;">
                          <th style="text-align:left;padding:6px;border:1px solid #ddd;">Parameter</th>
                          <th style="text-align:left;padding:6px;border:1px solid #ddd;">Value</th>
                          <th style="text-align:left;padding:6px;border:1px solid #ddd;">Reference</th>
                          <th style="text-align:left;padding:6px;border:1px solid #ddd;">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${objectiveRows.map(row => `
                          <tr>
                            <td style="padding:6px;border:1px solid #ddd;">${escapeHtml(row.parameter)}</td>
                            <td style="padding:6px;border:1px solid #ddd;">${escapeHtml(row.value)}</td>
                            <td style="padding:6px;border:1px solid #ddd;">${escapeHtml(row.reference)}</td>
                            <td style="padding:6px;border:1px solid #ddd;text-transform:capitalize;">${escapeHtml(row.status)}</td>
                          </tr>`).join('')}
                      </tbody>
                    </table>`
              : soapTextHtml(soap_notes.objective)
          }</div>
        </div>

        <div class="soap-section">
          <div class="soap-label">Assessment</div>
          <div class="soap-content">${soapTextHtml(soap_notes.assessment)}</div>
        </div>

        <div class="soap-section plan-section">
          <div class="soap-label">Plan</div>
          <div class="soap-content">${soapTextHtml(soap_notes.plan)}</div>
        </div>`}

        ${!abnormalFindings.length ? '' : `
        <div class="soap-section">
          <div class="soap-label">Abnormal Findings</div>
          <div class="soap-content">
            ${abnormalFindings.map((f) => {
              const finding = toDisplayText(f?.finding) || 'Unknown finding';
              const significance = toDisplayText(f?.significance);
              const severity = toDisplayText(f?.severity);
              return `
              <div style="margin-bottom:10px;padding:8px 10px;border-left:3px solid #e08a00;background:#fff8ec;">
                <div style="font-weight:600;">${escapeHtml(finding)}${severity ? ` <span style="font-weight:400;text-transform:capitalize;">(${escapeHtml(severity)})</span>` : ''}</div>
                ${significance ? `<div style="font-size:12px;margin-top:4px;white-space:pre-wrap;">${escapeHtml(significance)}</div>` : ''}
              </div>`;
            }).join('')}
          </div>
        </div>`}

        ${!nextSteps ? '' : `
        <div class="soap-section plan-section">
          <div class="soap-label">Next Steps</div>
          <div class="soap-content">${soapTextHtml(nextSteps)}</div>
        </div>`}

        ${!detailedGroups.length ? '' : `
        <div class="soap-section">
          <div class="soap-label">Detailed Results</div>
          <div class="soap-content">
            <table style="width:100%;border-collapse:collapse;font-size:13px;">
              <thead>
                <tr style="background:#f0f0f0;">
                  <th style="text-align:left;padding:6px;border:1px solid #ddd;">Test</th>
                  <th style="text-align:left;padding:6px;border:1px solid #ddd;">Result</th>
                  <th style="text-align:left;padding:6px;border:1px solid #ddd;">Reference</th>
                  <th style="text-align:left;padding:6px;border:1px solid #ddd;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${detailedGroups.map((g) => `
                  <tr>
                    <td style="padding:6px;border:1px solid #ddd;vertical-align:top;">${escapeHtml(g.test)}</td>
                    <td style="padding:6px;border:1px solid #ddd;vertical-align:top;">${
                      g.results.length > 1
                        ? `<ul style="margin:0;padding-left:16px;">${g.results.map((r) => `<li style="white-space:pre-wrap;">${escapeHtml(r || '-')}</li>`).join('')}</ul>`
                        : `<span style="white-space:pre-wrap;">${escapeHtml(g.results[0] || '-')}</span>`
                    }</td>
                    <td style="padding:6px;border:1px solid #ddd;vertical-align:top;">${escapeHtml(g.reference || 'N/A')}</td>
                    <td style="padding:6px;border:1px solid #ddd;vertical-align:top;">${escapeHtml(g.status)}</td>
                  </tr>`).join('')}
              </tbody>
            </table>
          </div>
        </div>`}

        <div class="footer">
          <p>Generated by meragoAnalyze | Printed on ${new Date().toLocaleString()}</p>
          <p style="margin-top: 5px;">This document is for clinical decision support only and should be reviewed by qualified healthcare professionals.</p>
        </div>
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } else {
      toast.error('Unable to open print window. Please check your popup blocker settings.');
    }
  };

  // Open Patient Summary preview modal
  const openPatientSummaryPreview = async () => {
    setLoadingPatientSummary(true);
    try {
      const response = await api.get(`/reports/${reportId}/patient-summary`);
      setPatientSummaryData(response.data);
      setShowPatientSummaryPreview(true);
    } catch (error) {
      toast.error('Failed to load patient summary');
    } finally {
      setLoadingPatientSummary(false);
    }
  };

  // Download Patient Summary as PDF
  const downloadPatientSummaryPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const response = await api.get(`/reports/${reportId}/patient-summary/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `patient-summary-${reportId.slice(0,8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
      setShowPatientSummaryPreview(false);
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  // Full automated pipeline: Parse -> Context -> Analyze in one step
  const handleFullProcess = async () => {
    if (!reportContent.trim()) {
      toast.error('Please paste report content before processing');
      return;
    }
    
    setProcessing(true);
    setShowProcessDialog(false);
    
    try {
      // Step 1: Parse with content
      toast.info('Step 1/2: Extracting lab values...');
      await api.post(`/reports/${reportId}/parse?report_content=${encodeURIComponent(reportContent)}`);
      
      // Step 2: Run AI analysis (patient context disabled)
      toast.info('Step 2/2: Running AI analysis...');
      await api.post(`/reports/${reportId}/analyze`);
      
      toast.success('Report processed successfully! SOAP notes generated.');
      fetchReportDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Processing failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleParse = async () => {
    setProcessing(true);
    try {
      await api.post(`/reports/${reportId}/parse`);
      toast.success('Report parsing initiated');
      fetchReportDetail();
    } catch (error) {
      toast.error('Failed to parse report');
    } finally {
      setProcessing(false);
    }
  };

  // Patient context fetch disabled
  // const handleFetchContext = async () => { ... };

  const handleAnalyze = async () => {
    setProcessing(true);
    try {
      const response = await api.post(`/reports/${reportId}/analyze`);
      toast.success(`Analysis complete - Urgency: ${response.data.urgency_level}`);
      fetchReportDetail();
    } catch (error) {
      toast.error('Failed to analyze report');
    } finally {
      setProcessing(false);
    }
  };

  const handleComplete = async () => {
    try {
      await api.patch(`/reports/${reportId}`, { status: 'completed' });
      toast.success('Report marked as completed');
      fetchReportDetail();
    } catch (error) {
      toast.error('Failed to update report');
    }
  };

  // Editable sections. These are hooks, so they run before the early returns below and
  // read through `data?.` — React requires the same hooks in the same order on every
  // render, including the loading and not-found ones.
  const canEditSections = SECTION_EDIT_ROLES.includes(user?.role);

  const analysisSection = useEditableSection({
    sectionKey: SECTIONS.clinicalAnalysis,
    baselineText: serializeClinicalAnalysis(data?.clinical_analysis),
    versions: sectionVersions[SECTIONS.clinicalAnalysis],
    canEdit: canEditSections && Boolean(data?.clinical_analysis),
    onSave: saveSectionVersion,
  });

  const summarySection = useEditableSection({
    sectionKey: SECTIONS.patientSummary,
    baselineText: serializePatientSummary(data?.patient_summary),
    versions: sectionVersions[SECTIONS.patientSummary],
    canEdit: canEditSections && Boolean(data?.patient_summary),
    onSave: saveSectionVersion,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Report not found</p>
        <Button variant="link" asChild className="mt-2">
          <Link to="/reports">Back to Report Queue</Link>
        </Button>
      </div>
    );
  }

  const { report, patient, observations, narrative_findings, context, clinical_analysis, patient_summary, alerts, tasks, audit_events } = data;
  const StatusIcon = statusConfig[report?.status]?.icon || FileText;
  const isRadiologyReport = report?.report_type === 'radiology' || report?.file_type === 'image';

  // What the two editable sections render. Once edited, the saved text is parsed back
  // into the AI's own shape and passed through the same JSX, so the SOAP card, the
  // objective table and the plan callout survive an edit. Confidence and urgency stay
  // on the original record — they are model outputs, not something a clinician edits.
  // The preview tabulates the objective whenever it can, whether it came from the AI as
  // structured data or from a clinician as result lines.
  const soapPreviewObjectiveRows = objectiveTableRows(analysisForDisplay?.soap_notes?.objective);

  // A radiology report's next steps and detailed findings are clinician-facing, so they
  // sit under Clinical Analysis rather than in the patient's summary. Lab reports keep
  // them where they are: their detail is the observations table the patient summary is
  // built around.
  const analysisNextSteps = isRadiologyReport ? toDisplayText(summaryForDisplay?.next_steps_text) : '';
  const analysisDetailedResults = isRadiologyReport ? groupDetailedResults(data.detailed_results) : [];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="report-detail-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/reports')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                Report Detail
              </h1>
              <Badge className={statusConfig[report?.status]?.class}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusConfig[report?.status]?.label}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              ID: {report?.report_id?.slice(0, 8)} | Type: {report?.report_type} | {new Date(report?.report_date).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {/* View Original Document button - only show if file_ref exists */}
          {report?.file_ref && (
            <Button
              variant="outline"
              onClick={loadOriginalDocument}
              disabled={loadingDoc}
              data-testid="view-original-btn"
            >
              {loadingDoc ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : report?.file_type === 'image' || report?.report_type === 'radiology' ? (
                <Image className="w-4 h-4 mr-2" />
              ) : (
                <Eye className="w-4 h-4 mr-2" />
              )}
              View Original
            </Button>
          )}
          
          {/* Reports are auto-processed on upload. Show status message for incomplete reports */}
          {['new', 'awaiting_context', 'analyzing'].includes(report?.status) && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              Processing...
            </Badge>
          )}
          
          {/* Reprocess button for reports with raw content */}
          {report?.raw_content && (
            <Button
              variant="outline"
              disabled={processing}
              onClick={async () => {
                setProcessing(true);
                try {
                  const response = await api.post(`/reports/${reportId}/reprocess`);
                  toast.success(`Reprocessed! ${response.data.observations_extracted} observations extracted.`);
                  fetchReportDetail();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Reprocessing failed');
                } finally {
                  setProcessing(false);
                }
              }}
              data-testid="reprocess-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Reprocessing...
                </>
              ) : (
                <>
                  <Activity className="w-4 h-4 mr-2" />
                  Reprocess
                </>
              )}
            </Button>
          )}
          
          {report?.status === 'awaiting_review' && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="complete-btn">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark Complete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Complete Review?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will finalize the analysis and store results to HealthVault. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleComplete}>Complete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Processing Status Banner */}
      {['new', 'awaiting_context', 'analyzing', 'parsing'].includes(report?.status) && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
          <Loader2 className="w-5 h-5 text-blue-600 mt-0.5 animate-spin" />
          <div className="flex-1">
            <p className="font-medium text-blue-800">Report Processing</p>
            <p className="text-sm text-blue-700 mt-1">
              This report is being automatically processed. Lab values are being extracted and SOAP notes are being generated. This page will update when complete.
            </p>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Patient Info & Context */}
        <div className="space-y-6">
          {/* Patient Panel */}
          <Card data-testid="patient-panel">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <User className="w-4 h-4" />
                Patient Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {patient ? (
                <>
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{patient.full_name}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-muted-foreground">DOB</p>
                      <p className="font-medium">{patient.dob ? new Date(patient.dob).toLocaleDateString() : '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Sex</p>
                      <p className="font-medium">{(patient.sex || patient.gender || report?.patient_info?.sex || '-').toString().charAt(0).toUpperCase() + (patient.sex || patient.gender || report?.patient_info?.sex || '-').toString().slice(1).toLowerCase()}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">MRN</p>
                    <p className="font-mono text-sm">{patient.mrn_member_id || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">HealthVault Ref</p>
                    <p className="font-mono text-sm">{patient.health_vault_ref || '-'}</p>
                  </div>
                </>
              ) : report?.patient_info ? (
                <>
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{report.patient_name || '-'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Age</p>
                      <p className="font-medium">{report.patient_info.age || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Sex</p>
                      <p className="font-medium">{(report.patient_info.sex || '-').toString().charAt(0).toUpperCase() + (report.patient_info.sex || '-').toString().slice(1).toLowerCase()}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">MRN</p>
                    <p className="font-mono text-sm">{report.patient_mrn || '-'}</p>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <User className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No patient linked</p>
                  <Badge variant="outline" className="mt-2 bg-amber-50 text-amber-700 border-amber-200">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Identity Unresolved
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Patient Context Panel - Hidden as per requirement
          <Card data-testid="context-panel">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                Patient Context
              </CardTitle>
            </CardHeader>
            <CardContent>
              {context ? (
                <Tabs defaultValue="conditions" className="w-full">
                  <TabsList className="grid w-full grid-cols-4 h-9">
                    <TabsTrigger value="conditions" className="text-xs">Conditions</TabsTrigger>
                    <TabsTrigger value="medications" className="text-xs">Meds</TabsTrigger>
                    <TabsTrigger value="vitals" className="text-xs">Vitals</TabsTrigger>
                    <TabsTrigger value="prior" className="text-xs">Prior</TabsTrigger>
                  </TabsList>
                  <TabsContent value="conditions" className="mt-3">
                    <ScrollArea className="h-32">
                      {context.conditions_json?.length > 0 ? (
                        <div className="space-y-2">
                          {context.conditions_json.map((c, i) => (
                            <div key={i} className="text-sm p-2 bg-slate-50 rounded">
                              <p className="font-medium">{c.condition}</p>
                              <p className="text-xs text-muted-foreground">{c.icd_code} | {c.status}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No conditions recorded</p>
                      )}
                    </ScrollArea>
                  </TabsContent>
                  <TabsContent value="medications" className="mt-3">
                    <ScrollArea className="h-32">
                      {context.medications_json?.length > 0 ? (
                        <div className="space-y-2">
                          {context.medications_json.map((m, i) => (
                            <div key={i} className="text-sm p-2 bg-slate-50 rounded flex items-center gap-2">
                              <Pill className="w-4 h-4 text-blue-500" />
                              <div>
                                <p className="font-medium">{m.name} {m.dose}</p>
                                <p className="text-xs text-muted-foreground">{m.frequency}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No medications recorded</p>
                      )}
                    </ScrollArea>
                  </TabsContent>
                  <TabsContent value="vitals" className="mt-3">
                    <ScrollArea className="h-32">
                      {context.vitals_json?.length > 0 ? (
                        <div className="space-y-2">
                          {context.vitals_json.map((v, i) => (
                            <div key={i} className="text-sm p-2 bg-slate-50 rounded flex items-center gap-2">
                              <Heart className="w-4 h-4 text-red-500" />
                              <div>
                                <p className="font-medium">{v.type}: {v.value} {v.unit}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No vitals recorded</p>
                      )}
                    </ScrollArea>
                  </TabsContent>
                  <TabsContent value="prior" className="mt-3">
                    <ScrollArea className="h-32">
                      {context.prior_results_json?.length > 0 ? (
                        <div className="space-y-2">
                          {context.prior_results_json.map((p, i) => (
                            <div key={i} className="text-sm p-2 bg-slate-50 rounded flex items-center gap-2">
                              <TestTube className="w-4 h-4 text-purple-500" />
                              <div>
                                <p className="font-medium">{p.test}: {p.value} {p.unit}</p>
                                <Badge variant="outline" className={`text-xs ${p.flag === 'abnormal' ? 'bg-orange-50 text-orange-700' : 'bg-green-50 text-green-700'}`}>
                                  {p.flag}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No prior results</p>
                      )}
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              ) : (
                <div className="text-center py-6">
                  <ClipboardList className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Context not yet retrieved</p>
                </div>
              )}
            </CardContent>
          </Card>
          */}
        </div>

        {/* Center Column - Observations & Findings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Observations Table - Collapsible (collapsed by default) */}
          <Collapsible defaultOpen={false}>
            <Card data-testid="observations-panel">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="p-0 h-auto hover:bg-transparent">
                      <CardTitle className="text-base font-semibold flex items-center gap-2 cursor-pointer">
                        <TestTube className="w-4 h-4" />
                        Extracted Observations
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      </CardTitle>
                    </Button>
                  </CollapsibleTrigger>
                  <Badge variant="outline">{observations?.length || 0} items</Badge>
                </div>
              </CardHeader>
              <CollapsibleContent>
                <CardContent>
                  {observations?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>Test</TableHead>
                            <TableHead>Value</TableHead>
                            <TableHead>Reference</TableHead>
                            <TableHead>Flag</TableHead>
                            <TableHead className="w-[80px]">Trend</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {observations.map((obs) => {
                            // Check if there's prior data for this test (from context)
                            const hasPriorData = context?.prior_results_json?.some(
                              p => p.test?.toLowerCase() === obs.test_name?.toLowerCase()
                            );
                            return (
                              <TableRow key={obs.observation_id} data-testid={`obs-row-${obs.observation_id}`}>
                                <TableCell className="font-medium">{obs.test_name}</TableCell>
                                <TableCell>
                                  <span className={['abnormal', 'critical'].includes(obs.flag) ? 'font-semibold text-red-600' : ''}>
                                    {obs.value_text} {obs.unit}
                                  </span>
                                </TableCell>
                                <TableCell className="text-muted-foreground text-sm">
                                  {formatReferenceRange(obs)}
                                </TableCell>
                                <TableCell>
                                  {obs.flag && (
                                    <Badge className={`border ${flagConfig[obs.flag]?.class}`}>
                                      {obs.flag === 'critical' && <AlertCircle className="w-3 h-3 mr-1" />}
                                      {flagConfig[obs.flag]?.label}
                                      {['abnormal', 'borderline'].includes(obs.flag) ? flagDirection(obs) : ''}
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {hasPriorData ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                      onClick={() => {
                                        toast.info(`Trend analysis for ${obs.test_name} - Feature coming soon!`);
                                      }}
                                      title="View historical trend"
                                    >
                                      <TrendingUp className="w-4 h-4" />
                                    </Button>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <TestTube className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No observations extracted yet</p>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Clinical Analysis */}
          {clinical_analysis && (
            <Card data-testid="analysis-panel">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Stethoscope className="w-4 h-4" />
                    Clinical Analysis
                  </CardTitle>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <Badge variant="outline">
                      Confidence: {(clinical_analysis.confidence_score * 100).toFixed(0)}%
                    </Badge>
                    <Badge className={`border ${flagConfig[clinical_analysis.urgency_level]?.class || 'bg-slate-100'}`}>
                      {clinical_analysis.urgency_level === 'critical' && <AlertTriangle className="w-3 h-3 mr-1" />}
                      {clinical_analysis.urgency_level?.toUpperCase()}
                    </Badge>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={openSOAPPreview}
                      className="h-7 text-xs"
                      data-testid="print-soap-notes-btn"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      Preview PDF
                    </Button>
                    {analysisSection.controls}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Safety Notice */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-800">Clinical Decision Support Only</p>
                      <p className="text-blue-700 text-xs mt-0.5">
                        This analysis is for decision support. It does not provide definitive diagnoses or prescribe treatment.
                      </p>
                    </div>
                  </div>
                </div>

                {!analysisSection.showStructured && analysisSection.body}

                {analysisSection.showStructured && (
                <>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Clinician Summary</p>
                  <p className="text-sm whitespace-pre-wrap">{displayTextOr(analysisForDisplay.clinician_summary)}</p>
                </div>

                {/* SOAP Notes Section */}
                {analysisForDisplay.soap_notes && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 border-b flex items-center justify-between">
                      <p className="text-sm font-semibold flex items-center gap-2">
                        <ClipboardList className="w-4 h-4" />
                        SOAP Notes
                      </p>
                    </div>
                    <div className="divide-y">
                      <div className="p-3">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Subjective</p>
                        <p className="text-sm whitespace-pre-wrap">{displayTextOr(analysisForDisplay.soap_notes.subjective)}</p>
                      </div>
                      <div className="p-3">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Objective</p>
                        {/* Formatted table for objective data */}
                        {analysisForDisplay.soap_notes.objective ? (
                          <div className="bg-slate-50 rounded-lg p-3 overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                              <thead>
                                <tr className="bg-slate-100">
                                  <th className="text-left p-2 font-semibold text-slate-700 border-b-2 border-slate-300 w-2/5">Parameter</th>
                                  <th className="text-left p-2 font-semibold text-slate-700 border-b-2 border-slate-300 w-1/4">Value</th>
                                  <th className="text-left p-2 font-semibold text-slate-700 border-b-2 border-slate-300 w-1/4">Reference</th>
                                  <th className="text-left p-2 font-semibold text-slate-700 border-b-2 border-slate-300">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  // Check if objective is already structured array or needs parsing
                                  const objective = analysisForDisplay.soap_notes.objective;
                                  let results = [];
                                  
                                  if (Array.isArray(objective)) {
                                    // New format: already structured array
                                    results = objective.map(item => ({
                                      parameter: objectiveCell(item, ['parameter', 'name'], 'Unknown'),
                                      value: objectiveCell(item, ['value'], '-'),
                                      reference: objectiveCell(item, ['reference', 'ref'], 'N/A'),
                                      status: objectiveCell(item, ['status'], 'normal')
                                    }));
                                  } else if (typeof objective === 'string') {
                                    // Legacy format: parse text
                                    const lines = objective.split('\n').filter(line => line.trim());
                                    lines.forEach(line => {
                                      // Try to parse: "Test Name: Value (Ref: Range) - STATUS"
                                      const match = line.match(/^(.+?):\s*(.+?)\s*(?:\(Ref:\s*([^)]+)\))?\s*-\s*(\w+)$/i);
                                      if (match) {
                                        results.push({
                                          parameter: match[1].trim(),
                                          value: match[2].trim(),
                                          reference: match[3] ? match[3].trim() : 'N/A',
                                          status: match[4].trim().toLowerCase()
                                        });
                                      } else {
                                        // Fallback: just show the line
                                        results.push({
                                          parameter: line.trim(),
                                          value: '-',
                                          reference: 'N/A',
                                          status: 'noted'
                                        });
                                      }
                                    });
                                  }
                                  
                                  if (results.length === 0) {
                                    return (
                                      <tr>
                                        <td colSpan="4" className="p-3 text-center text-slate-500">No data available</td>
                                      </tr>
                                    );
                                  }
                                  
                                  return results.map((row, idx) => {
                                    const isAbnormal = ['low', 'high', 'critical', 'abnormal', 'elevated'].some(s => 
                                      row.status.toLowerCase().includes(s)
                                    );
                                    
                                    return (
                                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                        <td className="p-2 border-b border-slate-200 text-slate-700 font-medium">{row.parameter}</td>
                                        <td className="p-2 border-b border-slate-200 font-mono text-slate-900">{row.value}</td>
                                        <td className="p-2 border-b border-slate-200 text-slate-500 text-xs">{row.reference}</td>
                                        <td className={`p-2 border-b border-slate-200 ${isAbnormal ? 'text-red-600 font-semibold' : 'text-green-600'}`}>
                                          <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${isAbnormal ? 'bg-red-500' : 'bg-green-500'}`}></span>
                                          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                                        </td>
                                      </tr>
                                    );
                                  });
                                })()}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">Not available</p>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Assessment</p>
                        <p className="text-sm whitespace-pre-wrap">{displayTextOr(analysisForDisplay.soap_notes.assessment)}</p>
                      </div>
                      <div className="p-3 bg-blue-50">
                        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">Plan</p>
                        <p className="text-sm text-blue-800 whitespace-pre-wrap">{displayTextOr(analysisForDisplay.soap_notes.plan)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {analysisForDisplay.abnormal_findings_json?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Abnormal Findings</p>
                    <div className="space-y-2">
                      {analysisForDisplay.abnormal_findings_json.map((f, i) => {
                        const severity = toDisplayText(f?.severity);
                        const significance = toDisplayText(f?.significance);
                        return (
                          <div key={i} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                            <p className="font-medium text-sm text-orange-800">{displayTextOr(f?.finding, 'Unknown finding')}</p>
                            {significance && (
                              <p className="text-xs text-orange-700 mt-1 whitespace-pre-wrap">{significance}</p>
                            )}
                            {/* An analysis without a severity rendered an empty pill. */}
                            {severity && (
                              <Badge className={`mt-2 text-xs capitalize ${flagConfig[severity]?.class || ''}`}>
                                {severity}
                              </Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Radiology next steps and detailed findings, moved here from the
                    patient summary. Outside the edit fragment on purpose: they are the
                    report's own findings, not part of the analysis text a clinician
                    edits and versions. */}
                {analysisSection.footer}
                </>
                )}

                {analysisNextSteps && (
                  <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">Next Steps</p>
                    <p className="text-sm text-blue-900 whitespace-pre-wrap">{analysisNextSteps}</p>
                  </div>
                )}

                {analysisDetailedResults.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Detailed Results</p>
                    <div className="bg-slate-50 rounded-lg overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="text-left p-2 font-semibold">Test</th>
                            <th className="text-left p-2 font-semibold">Result</th>
                            <th className="text-left p-2 font-semibold">Reference</th>
                            <th className="text-left p-2 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analysisDetailedResults.map((group, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="p-2 align-top">{group.test}</td>
                              <td className="p-2 align-top">
                                {group.results.length > 1 ? (
                                  <ul className="list-disc pl-4 space-y-1">
                                    {group.results.map((result, j) => (
                                      <li key={j} className="whitespace-pre-wrap">{result || '-'}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span className="whitespace-pre-wrap">{group.results[0] || '-'}</span>
                                )}
                              </td>
                              <td className="p-2 text-muted-foreground align-top">{group.reference || 'N/A'}</td>
                              <td className={`p-2 align-top ${group.status.toLowerCase() === 'normal' ? 'text-green-600' : 'text-orange-600 font-medium'}`}>
                                {group.status}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <Separator />

                {/* Clinician Note — an internal field, not AI-generated content, so it
                    stays editable and visible regardless of section version state. */}
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Internal Note (Not Patient-Facing)</p>
                  <Textarea
                    placeholder="Add internal clinical notes..."
                    value={clinicianNote}
                    onChange={(e) => setClinicianNote(e.target.value)}
                    className="min-h-[80px]"
                    data-testid="clinician-note-input"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Patient Summary */}
          {patient_summary && (
            <Card data-testid="patient-summary-panel">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Patient Summary (Plain Language)
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const response = await api.get(`/reports/${reportId}/patient-summary`);
                          const isRadiology = response.data.report_info?.is_radiology;
                          const title = isRadiology ? 'PATIENT RADIOLOGY REPORT SUMMARY' : 'PATIENT LAB REPORT SUMMARY';
                          const resultsLabel = isRadiology ? 'DETAILED FINDINGS' : 'DETAILED RESULTS';
                          const normalLabel = isRadiology ? 'NORMAL FINDINGS' : 'NORMAL RESULTS';
                          const abnormalLabel = isRadiology ? 'FINDINGS REQUIRING ATTENTION' : 'ABNORMAL RESULTS';
                          
                          let detailedResultsText = '';
                          if (response.data.detailed_results?.length > 0) {
                            if (isRadiology) {
                              // Grouped like the preview table and the PDF: a radiology
                              // summary has a row per finding, so listing them flat
                              // repeated "Impression:" for every impression.
                              detailedResultsText = groupDetailedResults(response.data.detailed_results)
                                .map(g => {
                                  const heading = `${g.test} [${g.status}]:`;
                                  const body = g.results.length > 1
                                    ? g.results.map(r => `- ${r}`).join('\n')
                                    : g.results[0] || '-';
                                  return `${heading}\n${body}`;
                                })
                                .join('\n\n');
                            } else {
                              detailedResultsText = response.data.detailed_results
                                .map(r => `${r.test}: ${r.result} (Ref: ${r.reference_range}) - ${r.status}`)
                                .join('\n');
                            }
                          }
                          
                          // An edited summary supersedes the AI's wording and already
                          // carries its own results and next-step text, so it replaces
                          // those blocks. Copying the superseded original would put
                          // uncorrected text in front of a patient.
                          const editedSummary = response.data.summary.edited_content;
                          const summaryBody = editedSummary
                            ? `SUMMARY:\n${editedSummary}`
                            : [
                                `SUMMARY:\n${response.data.summary.overview}`,
                                `${normalLabel}:\n${response.data.summary.normal_results?.join('\n') || 'None'}`,
                                `${abnormalLabel}:\n${response.data.summary.abnormal_results?.join('\n') || 'None'}`,
                                // Radiology next steps moved to Clinical Analysis.
                                !isRadiology && `NEXT STEPS:\n${response.data.summary.next_steps}`,
                              ].filter(Boolean).join('\n\n');

                          // Radiology detail moved to Clinical Analysis too, so the copied
                          // summary matches the preview and the PDF it sits beside.
                          const detailedBlock = isRadiology || !detailedResultsText
                            ? ''
                            : `\n\n${resultsLabel}:\n${detailedResultsText}`;

                          const text = `
${title}
Generated: ${new Date().toLocaleDateString()}
Report Date: ${response.data.report_info.report_date}

PATIENT: ${response.data.patient_info.name}
MRN: ${response.data.patient_info.mrn}

${summaryBody}${detailedBlock}

DISCLAIMER: ${response.data.disclaimer}
                          `.trim();
                          await navigator.clipboard.writeText(text);
                          toast.success('Patient summary copied to clipboard');
                        } catch (error) {
                          console.error('Copy error:', error);
                          toast.error('Failed to copy summary');
                        }
                      }}
                      data-testid="copy-summary-btn"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy
                    </Button>
                    <Button
                      size="sm"
                      variant="default"
                      onClick={openPatientSummaryPreview}
                      disabled={loadingPatientSummary}
                      data-testid="preview-patient-summary-btn"
                    >
                      {loadingPatientSummary ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Eye className="w-3 h-3 mr-1" />
                      )}
                      Preview PDF
                    </Button>
                    {summarySection.controls}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {!summarySection.showStructured && summarySection.body}

                {summarySection.showStructured && (
                <>
                <p className="text-sm whitespace-pre-wrap">{displayTextOr(summaryForDisplay.plain_language_summary)}</p>

                {summaryForDisplay.normal_items_json?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Normal Results</p>
                    <div className="flex flex-wrap gap-1">
                      {summaryForDisplay.normal_items_json.map((item, i) => (
                        <Badge key={i} variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                          {typeof item === 'object' ? item.item : item}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {summaryForDisplay.abnormal_items_json?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Abnormal Results</p>
                    <div className="flex flex-wrap gap-1">
                      {summaryForDisplay.abnormal_items_json.map((item, i) => (
                        <Badge key={i} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                          {typeof item === 'object' ? item.item : item}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {summaryForDisplay.borderline_items_json?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Borderline Results</p>
                    <div className="flex flex-wrap gap-1">
                      {summaryForDisplay.borderline_items_json.map((item, i) => (
                        <Badge key={i} variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs">
                          {typeof item === 'object' ? item.item : item}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Hidden for radiology: those next steps are clinician-facing and are
                    shown under Clinical Analysis instead. The section editor still
                    carries the text, so it stays editable there — and the copy under
                    Clinical Analysis follows that edit. */}
                {!isRadiologyReport && summaryForDisplay.next_steps_text && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-medium text-green-800">Recommended Next Steps</p>
                    <p className="text-sm text-green-700 mt-1">{displayTextOr(summaryForDisplay.next_steps_text)}</p>
                  </div>
                )}
                {summarySection.footer}
                </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Alerts & Tasks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Alerts */}
            <Card data-testid="alerts-panel">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {alerts?.length > 0 ? (
                  <div className="space-y-2">
                    {alerts.map((alert) => (
                      <div 
                        key={alert.alert_id}
                        className={`p-3 rounded-lg border ${
                          alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                          alert.severity === 'high' ? 'bg-orange-50 border-orange-200' :
                          'bg-yellow-50 border-yellow-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <Badge className={flagConfig[alert.severity]?.class}>
                              {alert.severity}
                            </Badge>
                            <p className="text-sm mt-1">{alert.message}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No alerts</p>
                )}
              </CardContent>
            </Card>

            {/* Audit Trail */}
            <Card data-testid="audit-panel">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Audit Trail
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-40">
                  {audit_events?.length > 0 ? (
                    <div className="space-y-2">
                      {audit_events.slice(0, 5).map((event, i) => (
                        <div key={i} className="text-sm p-2 bg-slate-50 rounded">
                          <p className="font-medium">{event.action_type}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(event.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No audit events</p>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Original Document Viewer Modal */}
      {showOriginalDoc && originalDocUrl && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="original-doc-modal">
          <div className="relative w-full max-w-6xl h-[90vh] bg-white rounded-lg shadow-xl flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50 rounded-t-lg">
              <div className="flex items-center gap-3">
                {report?.file_type === 'image' || report?.report_type === 'radiology' ? (
                  <Image className="w-5 h-5 text-slate-600" />
                ) : (
                  <FileText className="w-5 h-5 text-slate-600" />
                )}
                <div>
                  <h3 className="font-semibold text-slate-900">Original Document</h3>
                  <p className="text-xs text-muted-foreground">
                    {report?.original_filename || 'Uploaded file'} | {report?.report_type}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = originalDocUrl;
                    link.download = report?.original_filename || 'document';
                    link.click();
                  }}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeOriginalDoc}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* Document Content */}
            <div className="flex-1 overflow-auto bg-slate-100 p-4">
              {report?.file_type === 'image' || report?.report_type === 'radiology' ? (
                <div className="flex items-center justify-center h-full">
                  <img 
                    src={originalDocUrl} 
                    alt="Original radiology image"
                    className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                  />
                </div>
              ) : (
                <iframe
                  src={originalDocUrl}
                  className="w-full h-full rounded-lg shadow-lg bg-white"
                  title="Original PDF Document"
                />
              )}
            </div>

            {/* Footer with comparison hint */}
            <div className="px-4 py-2 border-t bg-slate-50 rounded-b-lg">
              <p className="text-xs text-muted-foreground text-center">
                Compare this original document with the AI-extracted observations on the left panel
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SOAP Notes Preview Modal */}
      {showSOAPPreview && analysisForDisplay && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-50 rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">SOAP Notes Preview</h3>
                  <p className="text-sm text-muted-foreground">
                    {patientIdentity(data).name} • {data?.report?.report_type || 'Report'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    downloadSOAPNotes();
                    setShowSOAPPreview(false);
                  }}
                  data-testid="download-soap-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download PDF
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowSOAPPreview(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-auto p-6">
              {/* Disclaimer */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Clinical Decision Support Only</p>
                    <p className="text-xs mt-1">This analysis is for decision support purposes. It does not provide definitive diagnoses or prescribe treatment.</p>
                  </div>
                </div>
              </div>

              {/* Patient Info */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Patient Information</p>
                <p className="font-semibold text-lg">{patientIdentity(data).name}</p>
                <p className="text-sm text-muted-foreground">
                  MRN: {patientIdentity(data).mrn} •{' '}
                  Report Date: {data?.report?.report_date ? new Date(data.report.report_date).toLocaleDateString() : 'N/A'}
                </p>
              </div>

              {/* Clinician Summary sits directly under Patient Information, and follows
                  the latest saved edit like every other analysis surface. */}
              {toDisplayText(analysisForDisplay.clinician_summary) && (
                <div className="mb-6">
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-2 pb-1 border-b">Clinician Summary</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {displayTextOr(analysisForDisplay.clinician_summary)}
                  </p>
                </div>
              )}

              {/* SOAP Sections — omitted entirely for an analysis that has none, rather
                  than shown as a run of "Not available" rows. */}
              {analysisForDisplay.soap_notes && (
              <div className="space-y-5">
                <div>
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-2 pb-1 border-b">Subjective</p>
                  <p className="text-sm text-slate-700 pl-2 whitespace-pre-wrap">{displayTextOr(analysisForDisplay.soap_notes.subjective)}</p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-2 pb-1 border-b">Objective</p>
                  {soapPreviewObjectiveRows ? (
                    <div className="bg-slate-50 rounded-lg p-3 overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="text-left p-2 font-semibold text-slate-700 border-b-2 border-slate-300 w-2/5">Parameter</th>
                            <th className="text-left p-2 font-semibold text-slate-700 border-b-2 border-slate-300 w-1/4">Value</th>
                            <th className="text-left p-2 font-semibold text-slate-700 border-b-2 border-slate-300 w-1/4">Reference</th>
                            <th className="text-left p-2 font-semibold text-slate-700 border-b-2 border-slate-300">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {soapPreviewObjectiveRows.map((row, idx) => (
                            <tr key={idx} className="border-b border-slate-200">
                              <td className="p-2 text-slate-700">{row.parameter}</td>
                              <td className="p-2 text-slate-700">{row.value}</td>
                              <td className="p-2 text-slate-700">{row.reference}</td>
                              <td className="p-2 text-slate-700 capitalize">{row.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-700 pl-2 whitespace-pre-wrap">{displayTextOr(analysisForDisplay.soap_notes.objective)}</p>
                  )}
                </div>

                <div>
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-2 pb-1 border-b">Assessment</p>
                  <p className="text-sm text-slate-700 pl-2 whitespace-pre-wrap">{displayTextOr(analysisForDisplay.soap_notes.assessment)}</p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                  <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">Plan</p>
                  <p className="text-sm text-blue-900 whitespace-pre-wrap">{displayTextOr(analysisForDisplay.soap_notes.plan)}</p>
                </div>
              </div>
              )}

              {/* Abnormal Findings — part of the analysis, so it belongs in the preview
                  and the printed PDF alongside the summary, not only on the page. */}
              {analysisForDisplay.abnormal_findings_json?.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-2 pb-1 border-b border-orange-200">Abnormal Findings</p>
                  <div className="space-y-2">
                    {analysisForDisplay.abnormal_findings_json.map((f, i) => {
                      const severity = toDisplayText(f?.severity);
                      const significance = toDisplayText(f?.significance);
                      return (
                        <div key={i} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                          <p className="font-medium text-sm text-orange-800">
                            {displayTextOr(f?.finding, 'Unknown finding')}
                            {severity && <span className="font-normal capitalize"> ({severity})</span>}
                          </p>
                          {significance && (
                            <p className="text-xs text-orange-700 mt-1 whitespace-pre-wrap">{significance}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {analysisNextSteps && (
                <div className="mt-6 bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                  <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">Next Steps</p>
                  <p className="text-sm text-blue-900 whitespace-pre-wrap">{analysisNextSteps}</p>
                </div>
              )}

              {analysisDetailedResults.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 pb-1 border-b">Detailed Results</p>
                  <div className="bg-slate-50 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left p-2 font-semibold">Test</th>
                          <th className="text-left p-2 font-semibold">Result</th>
                          <th className="text-left p-2 font-semibold">Reference</th>
                          <th className="text-left p-2 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysisDetailedResults.map((group, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="p-2 align-top">{group.test}</td>
                            <td className="p-2 align-top">
                              {group.results.length > 1 ? (
                                <ul className="list-disc pl-4 space-y-1">
                                  {group.results.map((result, j) => (
                                    <li key={j} className="whitespace-pre-wrap">{result || '-'}</li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="whitespace-pre-wrap">{group.results[0] || '-'}</span>
                              )}
                            </td>
                            <td className="p-2 text-muted-foreground align-top">{group.reference || 'N/A'}</td>
                            <td className={`p-2 align-top ${group.status.toLowerCase() === 'normal' ? 'text-green-600' : 'text-orange-600 font-medium'}`}>
                              {group.status}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 border-t bg-slate-50 rounded-b-lg flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Generated by meragoAnalyze • {new Date().toLocaleDateString()}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSOAPPreview(false)}
                >
                  Close
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    downloadSOAPNotes();
                    setShowSOAPPreview(false);
                  }}
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Print / Save as PDF
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Patient Summary Preview Modal */}
      {showPatientSummaryPreview && patientSummaryData && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-50 rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Patient Summary Preview</h3>
                  <p className="text-sm text-muted-foreground">
                    {patientSummaryData.patient_info?.name || 'Patient'} • Plain Language Report
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={downloadPatientSummaryPDF}
                  data-testid="download-patient-summary-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download PDF
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPatientSummaryPreview(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-auto p-6">
              {/* Disclaimer */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Important Notice</p>
                    <p className="text-xs mt-1">{patientSummaryData.disclaimer || 'This summary is for informational purposes only. Please consult with your healthcare provider for medical advice.'}</p>
                  </div>
                </div>
              </div>

              {/* Patient Info */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Patient Information</p>
                <p className="font-semibold text-lg">{patientSummaryData.patient_info?.name || 'Unknown Patient'}</p>
                <p className="text-sm text-muted-foreground">
                  MRN: {patientSummaryData.patient_info?.mrn || 'N/A'} • 
                  Report Date: {patientSummaryData.report_info?.report_date || 'N/A'}
                </p>
              </div>

              {/* Summary Overview. Once a clinician has rewritten this summary, their
                  latest version is what the patient sees — and because that text already
                  carries its own normal/abnormal/next-step wording, it stands in for
                  those blocks rather than repeating them. */}
              <div className="mb-6">
                <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-2 pb-1 border-b">Summary</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {displayTextOr(
                    patientSummaryData.summary?.edited_content || patientSummaryData.summary?.overview,
                    'No summary available',
                  )}
                </p>
                {patientSummaryData.summary?.edited_content && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Edited — Version {patientSummaryData.summary.edited_version}
                    {patientSummaryData.summary.edited_by ? ` · ${patientSummaryData.summary.edited_by}` : ''}
                  </p>
                )}
              </div>

              {/* Normal Results */}
              {!patientSummaryData.summary?.edited_content && patientSummaryData.summary?.normal_results?.length > 0 && (
                <div className="mb-6">
                  <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-2 pb-1 border-b border-green-200">Normal Results</p>
                  <div className="flex flex-wrap gap-2">
                    {patientSummaryData.summary.normal_results.map((item, i) => (
                      <Badge key={i} variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Abnormal Results */}
              {!patientSummaryData.summary?.edited_content && patientSummaryData.summary?.abnormal_results?.length > 0 && (
                <div className="mb-6">
                  <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-2 pb-1 border-b border-orange-200">Results Requiring Attention</p>
                  <div className="space-y-2">
                    {patientSummaryData.summary.abnormal_results.map((item, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-orange-800 bg-orange-50 rounded-lg p-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Next Steps and Detailed Results. Both are absent for radiology: its next
                  steps and its impressions/recommendations are written for a clinician,
                  so they live under Clinical Analysis and its PDF instead. */}
              {!isRadiologyReport && !patientSummaryData.summary?.edited_content && patientSummaryData.summary?.next_steps && (
                <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                  <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">Next Steps</p>
                  <p className="text-sm text-blue-900">{patientSummaryData.summary.next_steps}</p>
                </div>
              )}

              {/* Detailed Results */}
              {!isRadiologyReport && patientSummaryData.detailed_results?.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 pb-1 border-b">Detailed Results</p>
                  <div className="bg-slate-50 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left p-2 font-semibold">Test</th>
                          <th className="text-left p-2 font-semibold">Result</th>
                          <th className="text-left p-2 font-semibold">Reference</th>
                          <th className="text-left p-2 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupDetailedResults(patientSummaryData.detailed_results).map((group, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="p-2 align-top">{group.test}</td>
                            <td className="p-2 font-mono align-top">
                              {group.results.length > 1 ? (
                                <ul className="list-disc pl-4 space-y-1">
                                  {group.results.map((result, j) => (
                                    <li key={j} className="whitespace-pre-wrap">{result || '-'}</li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="whitespace-pre-wrap">{group.results[0] || '-'}</span>
                              )}
                            </td>
                            <td className="p-2 text-muted-foreground align-top">{group.reference || 'N/A'}</td>
                            <td className={`p-2 align-top ${group.status.toLowerCase() === 'normal' ? 'text-green-600' : 'text-orange-600 font-medium'}`}>
                              {group.status}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 border-t bg-slate-50 rounded-b-lg flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Generated by meragoAnalyze • {new Date().toLocaleDateString()}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPatientSummaryPreview(false)}
                >
                  Close
                </Button>
                <Button
                  size="sm"
                  onClick={downloadPatientSummaryPDF}
                >
                  <FileDown className="w-4 h-4 mr-1" />
                  Download PDF
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
